/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { globalRef } from '@amazon/vinyl-util'

interface PrefixedFullscreenElement {
    webkitRequestFullscreen?: () => Promise<void> | void
}

interface PrefixedFullscreenDocument {
    webkitFullscreenElement?: Element | null
    webkitExitFullscreen?: () => Promise<void> | void
}

const windowState = globalRef(() => ({ fullscreen: false, listeners: new Set<() => void>() }))

function setWindowFullscreen(value: boolean): void {
    windowState.value.fullscreen = value
    windowState.value.listeners.forEach((listener) => listener())
}

function inDesktopShell(): boolean {
    return '__TAURI_INTERNALS__' in globalThis
}

function isDocumentRoot(element: Element): boolean {
    return element === document.documentElement
}

/** The desktop shell's window full screen; rejects on a native build that does not allow it. */
async function setShellFullscreen(value: boolean): Promise<void> {
    const { getCurrentWindow } = await import('@tauri-apps/api/window')
    await getCurrentWindow().setFullscreen(value)
    setWindowFullscreen(value)
}

/**
 * Whether `element` can be made fullscreen here. The desktop app's WebKit webview has no element
 * fullscreen, so its whole document goes full screen through the shell's window instead.
 */
export function canRequestFullscreen(element: Element): boolean {
    return (
        (inDesktopShell() && isDocumentRoot(element)) ||
        typeof element.requestFullscreen === 'function' ||
        typeof (element as PrefixedFullscreenElement).webkitRequestFullscreen === 'function'
    )
}

/** Rejects when fullscreen is unsupported or denied; never throws synchronously. */
export async function requestFullscreen(element: Element): Promise<void> {
    if (inDesktopShell() && isDocumentRoot(element)) {
        try {
            await setShellFullscreen(true)
            return
        } catch {
            // an older native build lacks the window permission: use the DOM API below
        }
    }
    if (typeof element.requestFullscreen === 'function') {
        await element.requestFullscreen()
        return
    }
    const prefixed = (element as PrefixedFullscreenElement).webkitRequestFullscreen
    if (typeof prefixed !== 'function') throw new Error('fullscreen_unsupported')
    await prefixed.call(element)
}

export function getFullscreenElement(): Element | null {
    return (
        (windowState.value.fullscreen ? document.documentElement : null) ??
        document.fullscreenElement ??
        (document as PrefixedFullscreenDocument).webkitFullscreenElement ??
        null
    )
}

/** Rejects when not fullscreen or unsupported; never throws synchronously. */
export async function exitFullscreen(): Promise<void> {
    if (windowState.value.fullscreen) {
        await setShellFullscreen(false)
        return
    }
    if (typeof document.exitFullscreen === 'function') {
        await document.exitFullscreen()
        return
    }
    const prefixed = (document as PrefixedFullscreenDocument).webkitExitFullscreen
    if (typeof prefixed !== 'function') throw new Error('fullscreen_unsupported')
    await prefixed.call(document)
}

/** Subscribes to fullscreen changes of either flavor; returns the unsubscribe. */
export function onFullscreenChange(listener: () => void): () => void {
    document.addEventListener('fullscreenchange', listener)
    document.addEventListener('webkitfullscreenchange', listener)
    windowState.value.listeners.add(listener)
    return () => {
        windowState.value.listeners.delete(listener)
        document.removeEventListener('fullscreenchange', listener)
        document.removeEventListener('webkitfullscreenchange', listener)
    }
}
